<template>
  <!-- Notifications mount once at the top so $notify() works under
       either shell. -->
  <notifications
    group="notifications"
    position="top center"
    width="80%"
  />

  <!--
    Phase 1a shell switcher. `useMk3Shell` is sticky for the session —
    flipped on by visiting any URL with `?ui=mk3`, off by `?ui=mk2`.
    See src/stores/ui.ts. Old MK2 shell stays untouched as the default.
  -->
  <Mk3AppShell v-if="uiStore.useMk3Shell && !loading" />

  <div
    v-else
    class="app sidebar-fixed"
    :class="containerClasses"
  >
    <header class="app-header navbar">
      <!-- Mobile burger menu -->
      <button
        @click.stop="toggleMobileNav"
        class="navbar-toggler d-lg-none"
        type="button"
      >
        <span class="navbar-toggler-icon" />
      </button>

      <a
        class="navbar-brand"
        href="https://travelanalytics.com.au/"
        target="_blank"
      />

      <router-view name="header" />

      <UserBadge />
    </header>

    <Sidebar />

    <div v-if="loading">
      <i class="fa fa-spinner fa-spin" />
    </div>

    <main
      class="main"
      @click="toggleMainClick"
      v-else
    >
      <PrintLayout>
        <breadcrumb />
        <router-view />
      </PrintLayout>

      <footer class="terms pt-4 pb-4">
        <div class="d-flex justify-content-center flex-nowrap">
          <span class="pe-2">
            <a target="_blank" :href="termsOfUseURL">Terms Of Use</a>
          </span>
          <span class="ps-2 pe-2 border-left">
            <a target="_blank" :href="privacyPolicyURL">Privacy Policy</a>
          </span>
          <span class="ps-2 border-left">
            <a target="_blank">
              {{ copyrightText }} {{ copyrightYear }}
            </a>
          </span>
        </div>
      </footer>
    </main>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { storeToRefs } from 'pinia'

  import { useAccountsStore } from '@/stores/accounts'
  import { useTermsStore } from '@/stores/terms'
  import { useHeaderStore } from '@/stores/header'
  import { useTreeStore } from '@/stores/tree'
  import { useUiStore } from '@/stores/ui'

  import { Breadcrumb, Sidebar, UserBadge } from '@/components'
  import PrintLayout from '@/views/PrintLayout.vue'
  import Mk3AppShell from '@/layouts/mk3/AppShell.vue'
  import { checkLocalStore } from '@/utils'
  import { setChartAccent } from '@/utils/chartTheme'
  // `parseISO` / `termsLastUpdate` are only needed when the
  // Terms-of-Use gate (see watcher below) is re-enabled — restore the
  // imports alongside that change.
  import { termsOfUseURL, privacyPolicyURL } from '@/defs'

  import router from './router'

  const accountsStore = useAccountsStore()
  const uiStore = useUiStore()

  const {
    user,
  } = storeToRefs(accountsStore)

  // Apply the active TMC's chart accent colour (TAMK3-56) to the chart theme
  // on load and whenever it changes (e.g. after a TA Admin edits it in
  // Settings → TMC Details). Gated on the `chart_use_brand_colour` switch:
  // when off (the default), charts fall back to the standard MK3 palette even
  // though the picked brand colour stays saved — so switching back and forth
  // never loses it.
  const { clientsGetter } = storeToRefs(useTreeStore())
  watch(
    () => {
      const s = clientsGetter.value?.[user.value?.active_client]?.settings
      return s?.chart_use_brand_colour ? s?.chart_accent_color : null
    },
    (hex) => setChartAccent(hex ?? null),
    { immediate: true },
  )

  // Data
  const loading = ref(true)
  const sidebarHidden = ref(false)
  const sidebarMobileShow = ref(false)
  const copyrightText = '© Copyright Travel Analytics'

  // Computed
  const containerClasses = computed(() => {
    return {
      'sidebar-hidden': sidebarHidden.value,
      'sidebar-mobile-show': sidebarMobileShow.value
    }
  })

  const copyrightYear = computed(() => {
    const today = new Date()
    return today.getFullYear()
  })

  // Load global data
  Promise.all([
    useTermsStore().getLastUpdated(),
    useHeaderStore().getCountryData(),
    useTreeStore().getClientsDetail(),
  ]).then(() => {
    loading.value = false
    // Force router to re-resolve current route after loading completes
    // This fixes the blank page on initial login when router-view was hidden during loading
    const currentPath = router.currentRoute.value.fullPath
    router.replace(currentPath)
  })

  // Watchers — only run user watcher when user data exists
  watch(user, (user, oldUser) => {
    if (!user || !user.metadata) return

    // check if user change triggers local storage change
    if (import.meta.env.MODE !== 'test') { checkLocalStore(user) }

    // Terms-of-Use gate is currently disabled — see commit
    // history. Treat every user as having accepted so we never
    // redirect to /terms. The Terms.vue view, /terms route, and the
    // `accept-terms` metadata update endpoint are still in the
    // codebase; flipping `userAcceptedTerms` back to the parseISO
    // comparison below re-enables the flow when the new ToS is
    // ready.
    //   const dateAccepted = parseISO(user.metadata.terms_accepted_date)
    //   const lastUpdated = parseISO(termsLastUpdate)
    //   const userAcceptedTerms = (
    //     import.meta.env.MODE === 'test' || (dateAccepted >= lastUpdated)
    //   )
    const userAcceptedTerms = true

    // and that the user has access to any entities
    const userHasEntities = ((user.entities && user.entities.length > 0) || user.user_type === 'Client')
    if (!userAcceptedTerms) {
      router.push('/terms')
    } else if (!userHasEntities || user.clients.length === 0) {
      router.push('/no-entities')
    } else if (!user.active_client || (user.clients.indexOf(user.active_client) === -1)) {
      router.push('/client-selection')
    } else if (oldUser && (oldUser.active_client !== user.active_client)) {
      router.push({ name: 'Summary' })
    } else if (['/terms', '/no-entities'].indexOf(router.currentRoute.value.path) !== -1) {
      router.push({ name: 'Summary' })
    }
  }, { immediate: true, deep: true })

  // Methods
  function toggleMainClick () { if (sidebarMobileShow.value) sidebarMobileShow.value = false }
  function toggleMobileNav () { sidebarMobileShow.value = !sidebarMobileShow.value }
</script>

<style lang="scss">
  // Import Main styles for this application
  @import '../scss/style';

  // Fix: BVN modals render with display:block inline style even when hidden,
  // creating invisible full-screen overlays that block all pointer events.
  // Force hidden modals completely out of the way with !important to override inline styles.
  .modal:not(.show) {
    display: none !important;
  }

  // Ensure the main content area scrolls vertically
  .app {
    overflow-y: auto;
    height: 100vh;
  }
  .main {
    overflow-y: auto;
  }

  // Spacing between rows in dashboard views — single 12px gap
  .spaced-rows > .row + .row {
    margin-top: 0.75rem;
  }
</style>

<style lang="scss" scoped>
  .terms {
    a {
      color: #6b6b6b
    }
    @media print {
      // hide ToS
      display: none !important;
    }
  }
</style>
